import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import useThemeColor from "../../hooks/useThemeColor";
import useDarkMode from "../../hooks/useDarkMode";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import "../order/ManageOrderFilters.css";
import "./requisition-pages.css";
import swal from "sweetalert";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import { MANAGE_PAGE_SEARCH_DEBOUNCE_MS } from "../../utils/managePageConstants";

const statusOptions = [
  { value: "", label: "All Status" },
  { value: "Draft", label: "Draft" },
  { value: "Submitted", label: "Submitted" },
  { value: "Accepted", label: "Accepted" },
  { value: "Sent", label: "Sent" },
  { value: "Received", label: "Received" },
  { value: "Cancelled", label: "Cancelled" },
];

const sortByOptions = [
  { value: "CreatedDate", label: "Date Created" },
  { value: "Requisition_ID", label: "Requisition ID" },
  { value: "Requisition_Number", label: "Requisition Number" },
  { value: "DueDate", label: "Due Date" },
  { value: "Status", label: "Status" },
];

const sortOrderSelectOptions = [
  { value: "DESC", label: "Desc" },
  { value: "ASC", label: "Asc" },
];

const getStatusColor = (s) => {
  switch ((s || "").toLowerCase()) {
    case "draft":
      return "#6c757d";
    case "submitted":
      return "#ffc107";
    case "accepted":
      return "#17a2b8";
    case "sent":
      return "#007bff";
    case "received":
      return "#28a745";
    case "cancelled":
      return "#dc3545";
    default:
      return "#6c757d";
  }
};

const ManageRequisitions = () => {
  const navigate = useNavigate();
  const compID = useSelector((state) => state.auth.user?.userData?.compID);
  const currentUserName = useSelector((state) => state.auth.user?.userData?.userName);
  const profileData = useSelector((state) => state.auth.profileData);
  const parentCompId = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const isMain = !parentCompId || parseInt(parentCompId, 10) <= 0;
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [requisitions, setRequisitions] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState({ value: "10", label: "10" });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(statusOptions[0]);
  const [sortBy, setSortBy] = useState({ value: "CreatedDate", label: "Date Created" });
  const [sortOrder, setSortOrder] = useState({ value: "DESC", label: "Desc" });
  const [branchOptions, setBranchOptions] = useState([{ value: "", label: "All Branches" }]);
  const [selectedBranch, setSelectedBranch] = useState({ value: "", label: "All Branches" });
  const [branchMap, setBranchMap] = useState({});
  const [originalItemsCount, setOriginalItemsCount] = useState(0);

  const fetchBranches = useCallback(async () => {
    if (!isMain) return;
    try {
      const res = await axiosInstance.get(APIs.GET_BRANCHES);
      if (res?.data?.status && Array.isArray(res.data.data)) {
        const opts = [
          { value: "", label: "All Branches" },
          ...res.data.data.map((b) => ({
            value: String(b.compID ?? b.CompID),
            label: b.compName ?? b.CompName ?? `Branch ${b.compID ?? b.CompID}`,
          })),
        ];
        setBranchOptions(opts);
        const map = {};
        (res.data.data || []).forEach((b) => {
          const id = b.compID ?? b.CompID;
          map[id] = b.compName ?? b.CompName ?? `Branch ${id}`;
        });
        setBranchMap(map);
      }
    } catch (e) {
      console.warn("Failed to load branches", e);
    }
  }, [isMain]);

  const fetchRequisitions = useCallback(async () => {
    if (!compID) return;
    try {
      setLoading(true);
      const params = {
        compId: compID,
        pageNumber,
        pageSize: pageSize.value || "10",
        search: search?.trim() || "",
        sortBy: sortBy.value || "CreatedDate",
        sortOrder: sortOrder.value || "DESC",
        status: status?.value || "",
      };
      if (isMain && selectedBranch?.value) params.requestingBranchId = parseInt(selectedBranch.value, 10);
      const res = await axiosInstance.get(APIs.GET_REQUISITIONS_BY_COMPANY, { params });
      let data = res?.data?.data;
      
      // For parent company: filter out draft requisitions created by other branches
      if (isMain && Array.isArray(data)) {
        data = data.filter(req => {
          const reqStatus = (req.status ?? req.Status ?? "").toLowerCase();
          const requestingBranchId = req.requestingBranchCompID ?? req.RequestingBranchCompID;
          
          // If status is explicitly selected, respect it
          if (status?.value && status.value.toLowerCase() === "draft") {
            return reqStatus === "draft";
          }
          
          // For parent company view: exclude draft requisitions from other branches
          // Only show drafts if they belong to the parent company itself (requestingBranchId === compID)
          if (reqStatus === "draft" && requestingBranchId && requestingBranchId !== compID) {
            return false; // Hide draft from other branches
          }
          
          return true;
        });
      }
      
      setRequisitions(Array.isArray(data) ? data : []);
      setTotalPages(res?.data?.totalPages ?? 0);
      setTotalRecords(res?.data?.totalRecords ?? 0);
      if (!search.trim() && !status?.value && !selectedBranch?.value) {
        setOriginalItemsCount(Array.isArray(data) ? data.length : 0);
      }
    } catch (e) {
      console.error(e);
      showErrorToast("Failed to load requisitions");
      setRequisitions([]);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [compID, pageNumber, pageSize, search, status, selectedBranch, isMain, sortBy, sortOrder]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  useEffect(() => {
    const t = setTimeout(() => fetchRequisitions(), MANAGE_PAGE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [fetchRequisitions]);

  const clearFilters = () => {
    setSearch("");
    setStatus(statusOptions[0]);
    setSelectedBranch({ value: "", label: "All Branches" });
    setPageNumber(1);
  };

  const hasFiltersApplied = () => !!(status?.value || selectedBranch?.value);

  const handleCloneRequisition = async (e, sourceRequisitionId) => {
    e?.stopPropagation?.();
    const ok = await swal({
      title: "Clone requisition?",
      text: "A new requisition will be created with the same branch, due date, notes prefix, and line items (quantities reset for send/receive).",
      icon: "info",
      buttons: { cancel: "Cancel", confirm: "Clone" },
    });
    if (!ok) return;
    try {
      setLoading(true);
      const res = await axiosInstance.post(APIs.POST_CLONE_REQUISITION, {
        SourceRequisitionId: sourceRequisitionId,
        CompId: compID,
        CreatedBy: currentUserName || "System",
      });
      if (!res?.data?.status) {
        showErrorToast(res?.data?.message ?? "Clone failed.");
        return;
      }
      const created = res?.data?.data;
      const newId = created?.requisition_ID ?? created?.Requisition_ID;
      showSuccessToast("Requisition cloned.");
      if (newId) navigate(`/requisition/updateRequisitionItems/${newId}`);
    } catch (err) {
      console.error(err);
      showErrorToast(err?.response?.data?.message ?? "Clone failed.");
    } finally {
      setLoading(false);
    }
  };

  const renderNoDataMessage = () => {
    if (requisitions.length === 0 && (search.trim() || hasFiltersApplied())) {
      return (
        <div className="alert alert-info text-center mt-3">
          <i className="fa fa-info-circle me-2" />
          No requisitions found matching your filter criteria.
        </div>
      );
    }
    return null;
  };

  if (initialLoading) {
    return (
      <div className={`requisition-page ${isDarkMode ? "requisition-page--dark" : ""}`}>
        <Preloader />
      </div>
    );
  }

  return (
    <div className={`requisition-page ${isDarkMode ? "requisition-page--dark" : ""}`}>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Home</Link>
            </li>
            <li className="breadcrumb-item active">Requisitions</li>
          </ol>
          <h1 className="page-header mb-0">Requisitions</h1>
        </div>
        <div className="ms-auto">
          <button
            type="button"
            className="btn req-btn req-btn-primary"
            onClick={() => navigate("/requisition/createRequisition")}
          >
            <i className="fas fa-plus" />
            Create Requisition
          </button>
        </div>
      </div>

      <div className="card border-0" style={{ position: "relative", zIndex: 1 }}>
        <div className="tab-content p-3">
          <div className="tab-pane fade show active">
            <div
              className="card filter-card mb-3"
              style={{
                position: "relative",
                zIndex: 1,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                borderRadius: "12px",
                border: "none",
                boxShadow: `0 4px 20px ${themeColor}40`,
              }}
            >
              <div className="card-body p-3">
                <div className="row g-3 align-items-center">
                  <div className="col-xl-3 col-lg-3 col-md-6">
                    <div className="search-input filter-control">
                        <input
                          type="text"
                          className="filter-input form-control"
                          value={search}
                          onChange={(e) => {
                            setSearch(e.target.value);
                            setPageNumber(1);
                          }}
                          placeholder="Search by requisition number or ID..."
                          style={{
                            backgroundColor: isDarkMode ? "#2d3436" : "rgba(255, 255, 255, 0.9)",
                            border: isDarkMode ? "1px solid #404040" : "1px solid rgba(255, 255, 255, 0.3)",
                            color: isDarkMode ? "#ffffff" : "#333",
                            borderRadius: "8px",
                            padding: "8px 12px",
                            outline: "none",
                          }}
                        />
                      </div>
                    </div>
                    <div className="col-xl-2 col-lg-2 col-md-6">
                      <div className="filter-control">
                        <Select
                          value={status}
                          onChange={(v) => {
                            setStatus(v);
                            setPageNumber(1);
                          }}
                          options={statusOptions}
                          styles={dropdownStyles}
                          placeholder="All Status"
                          className="filter-dropdown"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          menuShouldBlockScroll={false}
                          menuShouldScrollIntoView={false}
                          closeMenuOnScroll={false}
                        />
                      </div>
                    </div>
                    {isMain && (
                      <div className="col-xl-2 col-lg-2 col-md-6">
                        <div className="filter-control">
                          <Select
                            value={selectedBranch}
                            onChange={(v) => {
                              setSelectedBranch(v);
                              setPageNumber(1);
                            }}
                            options={branchOptions}
                            styles={dropdownStyles}
                            placeholder="All Branches"
                            className="filter-dropdown"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            menuShouldBlockScroll={false}
                            menuShouldScrollIntoView={false}
                            closeMenuOnScroll={false}
                          />
                        </div>
                      </div>
                    )}
                    <div className="col-xl-2 col-lg-2 col-md-6">
                      <div className="filter-control">
                        <Select
                          value={sortBy}
                          onChange={setSortBy}
                          options={sortByOptions}
                          styles={dropdownStyles}
                          placeholder="Order By"
                          className="filter-dropdown"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          menuShouldBlockScroll={false}
                          menuShouldScrollIntoView={false}
                          closeMenuOnScroll={false}
                        />
                      </div>
                    </div>
                    <div className="col-xl-2 col-lg-2 col-md-6">
                      <div className="filter-control">
                        <Select
                          value={sortOrder}
                          onChange={(v) => {
                            setSortOrder(v);
                            setPageNumber(1);
                          }}
                          options={sortOrderSelectOptions}
                          styles={dropdownStyles}
                          placeholder="Sort order"
                          className="filter-dropdown"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          menuShouldBlockScroll={false}
                          menuShouldScrollIntoView={false}
                          closeMenuOnScroll={false}
                        />
                      </div>
                    </div>
                    <div className="col-xl-2 col-lg-2 col-md-12">
                      <button
                        className="clear-btn"
                        onClick={clearFilters}
                        title="Clear all filters"
                      >
                        <i className="fa fa-times me-1" />
                        Filter
                      </button>
                    </div>
                  </div>
                </div>
              </div>

            {!loading &&
              requisitions.length === 0 &&
              !search.trim() &&
              !hasFiltersApplied() &&
              originalItemsCount === 0 && (
                <div className="mb-4">
                  <AddButton
                    onClick={() => navigate("/requisition/createRequisition")}
                    title="Create Requisition"
                    fontSize="16px"
                  />
                </div>
              )}

            {!loading &&
              (requisitions.length > 0 || search.trim() || hasFiltersApplied() || originalItemsCount > 0) && (
                <>
                  {renderNoDataMessage()}
                  {requisitions.length > 0 && (
              <div
                className="table-responsive table-container"
                style={{
                  minHeight: "200px",
                  maxHeight: "450px",
                  overflowY: "auto",
                  overflowX: "auto",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <table className="table table-hover" style={{ position: "relative" }}>
                  <thead
                    className="bg-light sticky-header"
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 10000,
                      backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  >
                    <tr>
                      <th className="pt-2 pb-2">Requisition</th>
                      {isMain && <th className="pt-2 pb-2">Requesting Branch</th>}
                      <th className="pt-2 pb-2 text-center">Due Date</th>
                      <th className="pt-2 pb-2 text-center">Status</th>
                      <th className="pt-2 pb-2 text-center">Created</th>
                      <th className="pt-2 pb-2 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requisitions.map((r) => {
                      const rid = r.requisition_ID ?? r.Requisition_ID;
                      const reqNum = r.requisition_Number ?? r.Requisition_Number ?? `REQ-${rid}`;
                      const branchId = r.requestingBranchCompID ?? r.RequestingBranchCompID;
                      return (
                        <tr
                          key={rid}
                          draggable={false}
                          onClick={() => navigate(`/requisition/updateRequisition/${rid}`)}
                          style={{ cursor: "pointer" }}
                        >
                          <td className="align-middle">
                            <span className="fw-semibold">{reqNum}</span>
                          </td>
                          {isMain && (
                            <td className="align-middle">{branchMap[branchId] ?? branchId ?? "—"}</td>
                          )}
                          <td className="align-middle text-center">
                            {r.dueDate ?? r.DueDate
                              ? new Date(r.dueDate ?? r.DueDate).toLocaleDateString()
                              : "—"}
                          </td>
                          <td className="align-middle text-center">
                            <span
                              className="badge"
                              style={{
                                backgroundColor: getStatusColor(r.status ?? r.Status),
                                color: "white",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                fontSize: "11px",
                                fontWeight: "500",
                              }}
                            >
                              {r.status ?? r.Status ?? "—"}
                            </span>
                          </td>
                          <td className="align-middle text-center">
                            {r.createdDate ?? r.CreatedDate
                              ? new Date(r.createdDate ?? r.CreatedDate).toLocaleString()
                              : "—"}
                          </td>
                          <td className="align-middle text-center" onClick={(e) => e.stopPropagation()}>
                            <div className="d-flex justify-content-center gap-2 req-table-actions">
                              <Link
                                to={`/requisition/updateRequisition/${rid}`}
                                className="btn btn-sm btn-outline-primary"
                                title="View / Edit"
                              >
                                <i className="fa fa-edit" style={{ fontSize: "12px" }} />
                              </Link>
                              <Link
                                to={`/requisition/updateRequisitionItems/${rid}`}
                                className="btn btn-sm btn-outline-secondary"
                                title="Items"
                              >
                                <i className="fa fa-box" style={{ fontSize: "12px" }} />
                              </Link>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-info"
                                title="Clone"
                                onClick={(e) => handleCloneRequisition(e, rid)}
                              >
                                <i className="fa fa-copy" style={{ fontSize: "12px" }} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
                  <Pagination
                    totalRecords={totalRecords}
                    pageSize={pageSize}
                    pageNumber={pageNumber}
                    totalPages={totalPages}
                    setPageNumber={setPageNumber}
                    setPageSize={setPageSize}
                    themeColor={themeColor}
                  />
                </>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageRequisitions;
